import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Image from 'next/image'
import cx from 'classnames'

import styles from './TestimonialSliderSlim.module.scss'

const TestimonialSliderSlim = ({
  timeOutLength = 10000,
  logo = '',
  lightTheme = false,
  navPositionTop = false,
  quoteIcon = false,
  slides = [],
  multiSlides = false,
  fixedHeight = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [slidesToShow, setSlidesToShow] = useState(1)
  const [paused, setPaused] = useState(false)
  const timeoutRef = useRef(null)

  // Function to get the number of slides to show based on screen size
  const getSlidesToShow = () => {
    if (!multiSlides) return 1
    if (window.innerWidth >= 1024) return 3 // Desktop (3 slides)
    if (window.innerWidth >= 768) return 2 // Tablet (2 slides)
    return 1
  }

  useEffect(() => {
    const handleResize = () => {
      setSlidesToShow(getSlidesToShow())
    }

    handleResize()

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize)
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [multiSlides])

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (
      prevIndex === 0 ? slides.length - slidesToShow : (prevIndex - slidesToShow + slides.length) % slides.length))
  }

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + slidesToShow) % slides.length)
  }

  useEffect(() => {
    clearTimeout(timeoutRef.current)

    if (!paused) {
      timeoutRef.current = setTimeout(goToNext, timeOutLength)
    }

    return () => clearTimeout(timeoutRef.current)
  }, [currentIndex, paused, timeOutLength])

  const visibleSlides = []
  for (let i = 0; i < slidesToShow; i += 1) {
    visibleSlides.push(slides[(currentIndex + i) % slides.length])
  }

  return (
    <div
      className={cx(styles.sliderContainer, { [styles.lightTheme]: lightTheme }, { [styles.navPositionTop]: navPositionTop })}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className={styles.testimonialWrapper} style={fixedHeight ? { height: `${fixedHeight}px` } : {}}>
        {visibleSlides.map((slide) => (
          <div key={slide.author} className={cx(styles.testimonialItem, { [styles.quoteIcon]: quoteIcon })}>
            <div className={styles.slideHeader}>
              {slide.authorImage && <Image alt="author" className={styles.authorImage} src={slide.authorImage} style={{ height: 'auto' }} />}
              {logo && <Image alt="logo" className={styles.logo} src={logo} />}
            </div>
            <div className={styles.testimonialText}>
              <p>
                <span>“</span>{slide.text}<span>“</span>
              </p>
            </div>
            {
              slide.author &&
              <div className={styles.author}>
                <h4>{slide.author}</h4>
              </div>
            }
          </div>
        ))}
      </div>
      <div className={styles.buttonContainer}>
        <button className={styles.leftArrow} onClick={goToPrevious}>
          &#10094;
        </button>
        <button className={styles.rightArrow} onClick={goToNext}>
          &#10095;
        </button>
      </div>
    </div>
  )
}

TestimonialSliderSlim.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      authorImage: PropTypes.string,
      author: PropTypes.string,
      text: PropTypes.string.isRequired,
    }),
  ),
  navPositionTop: PropTypes.bool,
  quoteIcon: PropTypes.bool,
  timeOutLength: PropTypes.number,
  logo: PropTypes.string,
  lightTheme: PropTypes.bool,
  multiSlides: PropTypes.bool,
  fixedHeight: PropTypes.string,
}

export default TestimonialSliderSlim
